import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Tooltip, Button, Link } from '@mui/material';
import Masonry from '@mui/lab/Masonry';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GavelIcon from '@mui/icons-material/Gavel';
import SpeedIcon from '@mui/icons-material/Speed';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import BuildIcon from '@mui/icons-material/Build';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WorkflowProgress from './WorkflowProgress';

const AgentChat = ({ transcript, trace, history, onRerun, isRunning, hideProgress, noWrapper }) => {
  // Support both prop names for backwards compatibility
  const data = transcript || trace;
  const contentRefs = React.useRef({});

  // Auto-scroll to bottom when content updates
  React.useEffect(() => {
    if (data && data.length > 0) {
      data.forEach((_, idx) => {
        if (contentRefs.current[idx]) {
          contentRefs.current[idx].scrollTop = contentRefs.current[idx].scrollHeight;
        }
      });
    }
  }, [data]);

  const removeEmojis = (text) => {
    if (!text) return '';
    return text
      .replace(/[\u{1F600}-\u{1F64F}]/gu, '') 
      .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') 
      .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') 
      .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '') 
      .replace(/[\u{2600}-\u{26FF}]/gu, '')   
      .replace(/[\u{2700}-\u{27BF}]/gu, '')   
      .replace(/[\u{1F900}-\u{1F9FF}]/gu, '') 
      .trim();
  };

  const cleanMarkdown = (text) => {
    if (!text) return '';
    
    // Remove ALL markdown formatting
    let cleaned = text
      // Remove code blocks completely
      .replace(/```[\s\S]*?```/g, '')
      // Remove inline code backticks
      .replace(/`([^`]+)`/g, '$1')
      // Remove bold
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      // Remove italics
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      // Remove headers
      .replace(/^#+\s/gm, '')
      // Remove links but keep text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove emojis completely
      .replace(/[\u{1F000}-\u{1F9FF}]/gu, '')
      .replace(/[\u{2600}-\u{27BF}]/gu, '')
      .trim();
    
    return cleaned;
  };

  const formatContent = (text, accentColor) => {
    if (!text) return null;
    
    // Extract URLs BEFORE cleaning markdown (so we don't lose them)
    const urlRegex = /(https?:\/\/[^\s\)]+)/g;
    const urls = [...text.matchAll(urlRegex)].map(match => match[1]);
    
    // Now clean markdown
    const cleaned = cleanMarkdown(text);
    
    // Split by double newlines to preserve paragraph breaks
    const paragraphs = cleaned.split('\n\n').filter(p => p.trim());
    
    const content = paragraphs.map((paragraph, pIdx) => {
      const trimmed = paragraph.trim();
      
      // Tool execution indicators with proper formatting
      // Match various tool patterns: "Calling tool: web_search", "Calling tool: memory_search", etc.
      if (trimmed.match(/calling tool/i)) {
        const toolMatch = trimmed.match(/calling tool:\s*(\w+)/i);
        const toolName = toolMatch ? toolMatch[1] : 'tool';
        const ToolIcon = toolName.includes('web') ? LanguageIcon : StorageIcon;
        
        return (
          <Box key={pIdx} sx={{ mb: 1.5, p: 1.5, background: `${accentColor}10`, borderRadius: '8px', borderLeft: `3px solid ${accentColor}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon sx={{ color: accentColor, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: accentColor, fontSize: '0.85rem' }}>
              Calling Tool: {toolName.replace('_', ' ')}
            </Typography>
          </Box>
        );
      }
      
      // Tool executing
      if (trimmed.match(/executing/i) && !trimmed.match(/complete/i)) {
        const toolMatch = trimmed.match(/executing\s+(\w+)/i);
        const toolName = toolMatch ? toolMatch[1] : 'tool';
        const ToolIcon = toolName.includes('web') ? LanguageIcon : StorageIcon;
        
        return (
          <Box key={pIdx} sx={{ mb: 1.5, p: 1.5, background: `${accentColor}08`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToolIcon sx={{ color: accentColor, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: accentColor, fontSize: '0.85rem' }}>
              Executing {toolName.replace('_', ' ')}
            </Typography>
          </Box>
        );
      }
      
      // Tool complete
      if (trimmed.match(/complete/i) && !trimmed.match(/tool error/i)) {
        const toolMatch = trimmed.match(/(\w+)\s+complete/i);
        const toolName = toolMatch ? toolMatch[1] : 'Tool';
        
        return (
          <Box key={pIdx} sx={{ mb: 1.5, p: 1.5, background: `${accentColor}08`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: accentColor, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: accentColor, fontSize: '0.85rem' }}>
              {toolName.replace('_', ' ')} complete
            </Typography>
          </Box>
        );
      }
      
      // Tool error
      if (trimmed.match(/tool error/i)) {
        const cleanedError = trimmed.replace(/❌/g, '').replace(/🔧/g, '').replace(/⚙️/g, '').replace(/✅/g, '').trim();
        return (
          <Box key={pIdx} sx={{ mb: 1.5, p: 1.5, background: 'rgba(244, 67, 54, 0.1)', borderRadius: '8px', borderLeft: '3px solid #f44336', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#f44336', fontSize: '0.85rem' }}>
              {cleanedError}
            </Typography>
          </Box>
        );
      }
      
      // Split paragraph into lines for list formatting
      const lines = trimmed.split('\n').filter(line => line.trim());
      
      return (
        <Box key={pIdx} sx={{ mb: 1.5 }}>
          {lines.map((line, lIdx) => {
            const lineTrimmed = line.trim();
            
            // Check if line contains URL
            const lineUrls = [...lineTrimmed.matchAll(urlRegex)];
            if (lineUrls.length > 0) {
              // Replace URLs with clickable links
              let processedLine = lineTrimmed;
              lineUrls.forEach(match => {
                const url = match[1];
                processedLine = processedLine.replace(url, `[LINK:${url}]`);
              });
              
              const parts = processedLine.split(/\[LINK:(https?:\/\/[^\]]+)\]/);
              return (
                <Typography key={lIdx} variant="body2" sx={{ mb: 0.75, lineHeight: 1.8, fontSize: '0.875rem' }}>
                  {parts.map((part, i) => {
                    if (part.match(/^https?:\/\//)) {
                      return (
                        <Link key={i} href={part} target="_blank" rel="noopener noreferrer" sx={{ color: accentColor, textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.3, '&:hover': { textDecoration: 'underline' } }}>
                          {new URL(part).hostname.replace('www.', '')}
                          <OpenInNewIcon sx={{ fontSize: 12 }} />
                        </Link>
                      );
                    }
                    return <span key={i}>{part}</span>;
                  })}
                </Typography>
              );
            }
            
            // Bullet points
            if (lineTrimmed.startsWith('•') || lineTrimmed.startsWith('-') || lineTrimmed.startsWith('*')) {
              const content = lineTrimmed.replace(/^[•\-*]\s*/, '');
              return (
                <Box key={lIdx} sx={{ mb: 0.75, pl: 0.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, mt: 0.9, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ lineHeight: 1.8, flex: 1, color: 'text.primary', fontSize: '0.875rem' }}>
                    {content}
                  </Typography>
                </Box>
              );
            }
            
            // Numbered lists
            if (/^\d+[\.)]\s/.test(lineTrimmed)) {
              const num = lineTrimmed.match(/^\d+/)[0];
              const content = lineTrimmed.replace(/^\d+[\.)]\s*/, '');
              return (
                <Box key={lIdx} sx={{ mb: 0.75, pl: 0.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ color: accentColor, fontWeight: 700, minWidth: 24, lineHeight: 1.8, fontSize: '0.875rem' }}>
                    {num}.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.8, flex: 1, color: 'text.primary', fontSize: '0.875rem' }}>
                    {content}
                  </Typography>
                </Box>
              );
            }
            
            // Bold text (key-value pairs like "Source: value")
            if (lineTrimmed.includes(':')) {
              const colonIdx = lineTrimmed.indexOf(':');
              const key = lineTrimmed.substring(0, colonIdx);
              const value = lineTrimmed.substring(colonIdx + 1);
              
              if (key.length < 30 && key.length > 0) {
                return (
                  <Typography key={lIdx} variant="body2" sx={{ mb: 0.75, lineHeight: 1.8, fontSize: '0.875rem' }}>
                    <Box component="span" sx={{ fontWeight: 700, color: accentColor }}>{key}:</Box>
                    <Box component="span" sx={{ color: 'text.primary' }}>{value}</Box>
                  </Typography>
                );
              }
            }
            
            // Regular line
            return (
              <Typography key={lIdx} variant="body2" sx={{ mb: 0.75, lineHeight: 1.8, color: 'text.primary', fontSize: '0.875rem' }}>
                {lineTrimmed}
              </Typography>
            );
          })}
        </Box>
      );
    });
    
    // Add sources section at bottom if URLs found
    if (urls.length > 0) {
      content.push(
        <Box key="sources" sx={{ mt: 2, pt: 2, borderTop: `1px solid ${accentColor}30` }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: accentColor, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Sources ({urls.length})
          </Typography>
          {urls.map((url, i) => (
            <Link key={i} href={url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'text.secondary', fontSize: '0.75rem', textDecoration: 'none', '&:hover': { color: accentColor } }}>
              <OpenInNewIcon sx={{ fontSize: 14 }} />
              {new URL(url).hostname.replace('www.', '')}
            </Link>
          ))}
        </Box>
      );
    }
    
    return content;
  };

  const getAgentConfig = (agent) => {
    const agentLower = agent?.toLowerCase();
    
    const configs = {
      retriever: { 
        icon: SearchIcon, 
        label: 'Retriever', 
        color: '#3ad0de',
        gradient: 'linear-gradient(135deg, #3ad0de 0%, #2badb8 100%)' 
      },
      planner: { 
        icon: AssignmentIcon, 
        label: 'Planner', 
        color: '#D183C9',
        gradient: 'linear-gradient(135deg, #D183C9 0%, #a56ca0 100%)' 
      },
      judge: { 
        icon: GavelIcon, 
        label: 'Judge', 
        color: '#7595ff',
        gradient: 'linear-gradient(135deg, #7595ff 0%, #5974cb 100%)' 
      }
    };
    return configs[agentLower] || configs.retriever;
  };  if (!data || data.length === 0) {
    return (
      <Box sx={{ height: 'calc(100vh - 250px)', display: 'flex', flexDirection: 'column' }}>
        {/* Show stepper even when empty */}
        <Box sx={{ mb: 2 }}>
          <WorkflowProgress trace={[]} isRunning={false} />
        </Box>

        {/* Welcome screen */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 600, px: 4 }}>
            {/* <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
              {[
                { Icon: SearchIcon, color: '#3ad0de', delay: '0s' },
                { Icon: AssignmentIcon, color: '#D183C9', delay: '0.5s' },
                { Icon: GavelIcon, color: '#7595ff', delay: '1s' }
              ].map(({ Icon, color, delay }, i) => (
                <Box
                  key={i}
                  sx={{
                    background: `linear-gradient(135deg, ${color} 0%, ${color}aa 100%)`,
                    borderRadius: '12px',
                    width: 56,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${color}44`,
                    animation: `float 3s ease-in-out ${delay} infinite`,
                    '@keyframes float': {
                      '0%, 100%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-10px)' }
                    }
                  }}
                >
                  <Icon sx={{ color: '#fff', fontSize: 28 }} />
                </Box>
              ))}
            </Box> */}

            {/* <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, background: 'linear-gradient(135deg, #3ad0de 0%, #D183C9 50%, #7595ff 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Ready to Orchestrate
            </Typography> */}
            
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
              enter a prompt above to activate your multi-agent workflow 
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                { label: 'Retriever analyzes', color: '#3ad0de' },
                { label: 'Planner strategizes', color: '#D183C9' },
                { label: 'Judge decides', color: '#7595ff' }
              ].map((item, i) => (
                <Chip key={i} label={item.label} sx={{ background: `${item.color}26`, color: item.color, border: `1px solid ${item.color}4d` }} />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const handleExportResults = () => {
    const results = data.map(t => `${t.agent}: ${t.msg || t.output}`).join('\n\n');
    const blob = new Blob([results], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'workflow-results.txt';
    a.click();
  };

  return (
    <Box sx={{ height: noWrapper ? 'auto' : 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
      {/* Workflow Progress at top - always visible - hide if hideProgress prop is true */}
      {!hideProgress && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <WorkflowProgress trace={data} isRunning={isRunning} />
        </Box>
      )}

      {/* Scrollable content area */}
      <Box sx={{ flex: noWrapper ? 'none' : 1, overflowY: noWrapper ? 'visible' : 'auto', pr: 1, pb: 2, '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.03)', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#3ad0de', borderRadius: '4px' } }}>
        <Masonry columns={{ xs: 1, sm: 2, lg: 3 }} spacing={3}>
          <AnimatePresence>
            {data.map((t, idx) => {
              const config = getAgentConfig(t.agent);
              const AgentIcon = config.icon;
              const content = t.msg || t.output || '';

              return (
                <motion.div key={idx} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.3 }}>
                  <Card sx={{ borderLeft: `5px solid ${config.color}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', transition: 'all 0.2s ease', background: 'rgba(255,255,255,0.02)', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', transform: 'translateY(-1px)' } }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ background: config.gradient, borderRadius: '8px', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, boxShadow: `0 2px 6px ${config.color}20` }}>
                          <AgentIcon sx={{ color: '#fff', fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: config.color, mb: 0, fontSize: '0.95rem' }}>
                            {config.label}
                          </Typography>
                          {t.model && (<Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{t.model}</Typography>)}
                        </Box>
                      </Box>

                      <Box ref={(el) => contentRefs.current[idx] = el} sx={{ color: 'text.primary', fontSize: '0.875rem', maxHeight: '400px', overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.05)', borderRadius: '3px' }, '&::-webkit-scrollbar-thumb': { background: config.color, borderRadius: '3px' }, '&::-webkit-scrollbar-thumb:hover': { background: config.color, opacity: 0.9 } }}>
                        {formatContent(content, config.color)}
                      </Box>

                      {(t.time || t.cost) && (
                        <Box sx={{ pt: 0.75, mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {t.time && (<Chip icon={<SpeedIcon sx={{ fontSize: 14, color: `${config.color} !important` }} />} label={t.time} size="small" sx={{ background: `${config.color}15`, color: config.color, fontSize: '0.7rem', height: 22, fontWeight: 500, '& .MuiChip-icon': { marginLeft: '6px' } }} />)}
                          {t.cost && (<Chip icon={<AttachMoneyIcon sx={{ fontSize: 14, color: `${config.color} !important` }} />} label={t.cost} size="small" sx={{ background: `${config.color}15`, color: config.color, fontSize: '0.7rem', height: 22, fontWeight: 500, '& .MuiChip-icon': { marginLeft: '6px' } }} />)}
                          {t.done && (<Chip icon={<CheckCircleIcon sx={{ fontSize: 14, color: `${config.color} !important` }} />} label="Done" size="small" sx={{ background: `${config.color}20`, color: config.color, fontSize: '0.7rem', height: 22, fontWeight: 600, '& .MuiChip-icon': { marginLeft: '6px' } }} />)}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </Masonry>
      </Box>

      {/* Quick Actions Bar - OUTSIDE scrollable area, sticky at bottom - hide if noWrapper is true */}
      {!noWrapper && (
        <Box sx={{ pt: 0.75, mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportResults} sx={{ color: '#3ad0de', textTransform: 'none', fontSize: '0.75rem', '&:hover': { background: 'rgba(58, 208, 222, 0.1)' } }}>
            Export
          </Button>
          <Button size="small" startIcon={<RefreshIcon />} sx={{ color: '#D183C9', textTransform: 'none', fontSize: '0.75rem', '&:hover': { background: 'rgba(209, 131, 201, 0.1)' } }}>
            Clear
          </Button>
        </Box>

        {/* Session History */}
        {history && history.length > 0 && (
          <>
            <HistoryIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Recent:</Typography>
            {history.slice(-3).reverse().map((item, idx) => (
              <Tooltip key={idx} title={item.prompt} placement="top" arrow>
                <Chip label={item.prompt.substring(0, 25) + '...'} size="small" onClick={() => onRerun && onRerun(item.prompt)} sx={{ background: 'rgba(58, 208, 222, 0.15)', color: '#3ad0de', fontSize: '0.65rem', height: 22, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { background: 'rgba(58, 208, 222, 0.3)', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(58, 208, 222, 0.4)' } }} />
              </Tooltip>
            ))}
          </>
        )}
      </Box>
      )}
    </Box>
  );
};

export default AgentChat;
